// Customizable Area Start
import React from "react";
import { Box, styled, Typography } from "@mui/material";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import { Formik, Form } from "formik";
import ResetPasswordController, {
  Props,
  configJSON,
  validationSchema,
  passwordValidations,
} from "./ResetPasswordController.web";
import FullPageLoader from "../../../components/src/FullPageLoader.web";
import NavigationMenu from "../../navigationmenu/src/NavigationMenu.web";
import AppFooter from "../../../components/src/AppFooter.web";
import CustomPageLayout from "../../../components/src/CustomPageLayout.web";
import CustomTextField from "../../../components/src/CustomTextField.web";
import CustomButton from "../../../components/src/CustomButton.web";
import CustomInfoDialog from "../../../components/src/CustomInfoDialog.web";

const MainHeading = styled(Typography)({
  color: "#0F172A",
  fontSize: "1.75rem",
  fontWeight: 600,
  letterSpacing: "-0.12px",
  margin: "20px 0px 15px",
});

const SubHeading = styled(Typography)({
  color: "#64748B",
  fontSize: "1rem",
  fontWeight: 400,
  marginBottom: "15px",
});

const MainBtn = styled(CustomButton)({
  minHeight: "56px",
  marginTop: "30px",
});

const ErrorText = styled(Typography)({
  color: "#E11B23",
  fontSize: "0.8rem",
  fontWeight: 400,
  marginTop: "3px",
});

const PasswordCheckWrapper = styled(Box)({
  display: "flex",
  gap: "3px",
  alignItems: "center",
  marginBottom: "10px",
  "& p": {
    color: "#0F172A",
    fontSize: "0.875rem",
    fontWeight: 400,
  },
});

const CustomCheckIcon = styled(ErrorOutlineRoundedIcon, {
  shouldForwardProp: (prop) => prop !== "isValid",
})<{ isValid?: boolean }>(({ isValid }) => ({
  fill: isValid ? "#409046" : "#E11B23",
  height: "18px",
  width: "18px",
}));
// Customizable Area End

export default class ResetPassword extends ResetPasswordController {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }
  // Customizable Area Start
  render() {
    const { navigation } = this.props;
    const {
      isLoading,
      apiError,
      isInfoDialog,
      infoType,
      infoMessage,
    } = this.state;
    return (
      <>
        <FullPageLoader isLoading={isLoading} />
        <NavigationMenu navigation={navigation} id={""} />

        <CustomPageLayout>
          <Formik
            validateOnChange
            data-test-id="formik"
            initialValues={{ newPassword: "", confirmPassword: "" }}
            validationSchema={validationSchema}
            onSubmit={this.handleResetPasswordSubmit}
          >
            {({ errors, touched, values, handleChange, handleSubmit }) => (
              <Form
                data-test-id="form"
                onSubmit={(event) => {
                  event.preventDefault();
                  handleSubmit();
                }}
              >
                <MainHeading data-test-id="main-heading">
                  {configJSON.resetPasswordText}
                </MainHeading>
                <SubHeading>{configJSON.enterYourNewPasswordText}</SubHeading>
                <CustomTextField
                  fullWidth
                  type="password"
                  name="newPassword"
                  variant="outlined"
                  label="New Password"
                  placeholder="Enter your new password"
                  touched={touched.newPassword}
                  error={errors.newPassword}
                  value={values.newPassword}
                  handleChange={handleChange}
                />

                <CustomTextField
                  fullWidth
                  type="password"
                  name="confirmPassword"
                  variant="outlined"
                  label="Confirm Password"
                  wrapperSx={{ mt: "20px" }}
                  placeholder="Confirm your new password"
                  touched={touched.confirmPassword}
                  error={errors.confirmPassword}
                  value={values.confirmPassword}
                  handleChange={handleChange}
                />

                <Box mt={3}>
                  {passwordValidations.map((validation, index) => (
                    <PasswordCheckWrapper key={index}>
                      <CustomCheckIcon
                        isValid={validation.rule.test(values.newPassword)}
                      />
                      <Typography key={index} variant="body2">
                        {validation.label}
                      </Typography>
                    </PasswordCheckWrapper>
                  ))}
                </Box>

                {apiError && (
                  <ErrorText data-test-id="error-message">{apiError}</ErrorText>
                )}
                
                <MainBtn
                  fullWidth
                  type="submit"
                  disabled={this.isSubmitDisabled(values)}
                  customVariant="filled"
                >
                  {configJSON.resetPasswordText}
                </MainBtn>
              </Form>
            )}
          </Formik>
        </CustomPageLayout>
        <CustomInfoDialog
          data-test-id="info-dialog"
          isOpen={isInfoDialog}
          infoType={infoType}
          title={infoMessage}
          handleClose={this.closeInfoDialog}
        />
        <AppFooter navigation={navigation} />
      </>
    );
  }
  // Customizable Area End
}
